import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Alert, Snackbar, Typography, Divider, Link,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { epcApi, type EpcHandover } from '../../api/epc.api';
import { FileUploadField } from '../../components/FileUploadField';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const statusColor: Record<string, 'default' | 'info' | 'success' | 'error'> = { draft: 'default', submitted: 'info', accepted: 'success', rejected: 'error' };

export function EpcHandoversPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['epc-handovers', page, limit], queryFn: () => epcApi.listHandovers({ page, limit }) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-handovers'] }); qc.invalidateQueries({ queryKey: ['epc-handover'] }); };

  const columns: Column<EpcHandover>[] = [
    { key: 'handoverCode', label: 'Handover', width: 180 },
    { key: 'projectId', label: 'Project', render: (r) => `#${r.projectId}` },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" color={statusColor[r.status]} label={human(r.status)} /> },
    { key: 'clientRepresentative', label: 'Client Rep', render: (r) => r.clientRepresentative ?? '—' },
    { key: 'createdAt', label: 'Created', render: (r) => new Date(r.createdAt).toLocaleDateString() },
    { key: 'actions', label: '', width: 100, render: (r) => <Button size="small" onClick={() => setDetailId(r.id)}>Open</Button> },
  ];

  return (
    <>
      <PageHeader title="EPC Handovers" subtitle="Project handover with as-built documentation."
        action={hasPermission('epc_handover.create') ? <Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>New Handover</Button> : undefined} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />

      {open && <NewHandoverDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); setToast('Handover created'); refresh(); }} />}
      {detailId && <HandoverDetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={() => { setToast('Updated'); refresh(); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function NewHandoverDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ projectId: '', clientRepresentative: '', remarks: '' });
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => epcApi.createHandover({ projectId: Number(form.projectId), clientRepresentative: form.clientRepresentative || undefined, remarks: form.remarks || undefined }),
    onSuccess: onDone, onError: (e) => setError(extractApiError(e)),
  });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Handover</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <EpcProjectSelect value={form.projectId} onChange={(v) => setForm({ ...form, projectId: v })} sx={{ width: '100%' }} />
          <TextField label="Client Representative" value={form.clientRepresentative} onChange={(e) => setForm({ ...form, clientRepresentative: e.target.value })} fullWidth />
          <TextField label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.projectId || mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function HandoverDetailDialog({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const { hasPermission } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [doc, setDoc] = useState({ docType: 'as_built', fileName: '', fileUrl: '' });
  const { data: h } = useQuery({ queryKey: ['epc-handover', id], queryFn: () => epcApi.getHandover(id) });
  const qc = useQueryClient();
  const after = () => { qc.invalidateQueries({ queryKey: ['epc-handover', id] }); onChanged(); };
  const onErr = (e: unknown) => setError(extractApiError(e));

  const submitMut = useMutation({ mutationFn: () => epcApi.submitHandover(id), onSuccess: after, onError: onErr });
  const acceptMut = useMutation({ mutationFn: () => epcApi.acceptHandover(id), onSuccess: after, onError: onErr });
  const rejectMut = useMutation({ mutationFn: () => epcApi.rejectHandover(id, 'Rejected via portal'), onSuccess: after, onError: onErr });
  const docMut = useMutation({ mutationFn: () => epcApi.addDocument(id, doc), onSuccess: () => { setDoc({ docType: 'as_built', fileName: '', fileUrl: '' }); after(); }, onError: onErr });

  if (!h) return null;
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle><Stack direction="row" spacing={1.5} alignItems="center"><span>{h.handoverCode}</span><Chip size="small" color={statusColor[h.status]} label={human(h.status)} /></Stack></DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary">Project #{h.projectId} · {h.clientRepresentative ?? '—'}</Typography>

        <Divider textAlign="left" sx={{ my: 1.5 }}>As-built Documents</Divider>
        <Stack spacing={0.5}>
          {(h.documents ?? []).map((d) => <Link key={d.id} href={d.fileUrl} target="_blank">{human(d.docType)}: {d.fileName}</Link>)}
          {(h.documents ?? []).length === 0 && <Typography variant="body2" color="text.secondary">No documents yet.</Typography>}
        </Stack>
        {hasPermission('epc_handover.update') && h.status !== 'accepted' && (
          <Stack direction="row" spacing={1} mt={1} alignItems="center">
            <TextField select size="small" label="Type" value={doc.docType} onChange={(e) => setDoc({ ...doc, docType: e.target.value })} sx={{ minWidth: 150 }}>
              {['as_built', 'completion_certificate', 'test_report', 'drawing', 'photo', 'other'].map((t) => <MenuItem key={t} value={t}>{human(t)}</MenuItem>)}
            </TextField>
            <FileUploadField label="Document" value={doc.fileUrl || null} onChange={(url) => setDoc({ ...doc, fileUrl: url ?? '', fileName: (url ?? '').split('/').pop() || 'doc' })} />
            <Button size="small" disabled={!doc.fileUrl || docMut.isPending} onClick={() => docMut.mutate()}>Add</Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {h.status === 'draft' && hasPermission('epc_handover.submit') && <Button variant="contained" onClick={() => submitMut.mutate()} disabled={submitMut.isPending}>Submit</Button>}
        {h.status === 'submitted' && hasPermission('epc_handover.approve') && (
          <>
            <Button color="success" variant="contained" onClick={() => acceptMut.mutate()} disabled={acceptMut.isPending}>Accept</Button>
            <Button color="error" variant="outlined" onClick={() => rejectMut.mutate()} disabled={rejectMut.isPending}>Reject</Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
